import { Plus, RefreshCw, Save, Trash2 } from 'lucide-react'

import { Button, SelectField, StatusMessage } from '@krp/ui-kit'

import type { PriceType, PriceTypeMap, ShownError } from '../model/types'
import { ErrorBlock } from './parts'
import './sync.css'

export interface PriceMapDraft {
  firstResourceId: string
  firstPriceTypeKey: string
  secondResourceId: string
  secondPriceTypeKey: string
}

export interface PriceMapsVm {
  status: 'loading' | 'failed' | 'ready'
  loadError: ShownError | null
  /** Сопоставление, как оно будет сохранено: сохранённое службой с правками человека. */
  maps: PriceTypeMap[]
  /** Виды цен с одинаковым именем у двух источников, ещё не сопоставленные (сами не действуют — их подтверждает человек). */
  suggestions: PriceTypeMap[]
  sources: { resourceId: string; name: string }[]
  nameOf: (resourceId: string) => string
  /** Виды цен источника из его ленты цен; пусто — лента цен ещё не прочитана. */
  typesOf: (resourceId: string) => readonly PriceType[]
  /** Вид цены словами: имя из ленты источника, без имени — ключ. */
  typeName: (resourceId: string, key: string) => string
  draft: PriceMapDraft
  /** Почему новую строку добавить нельзя; `null` — можно. */
  draftProblem: string | null
  dirty: boolean
  busy: boolean
  error: ShownError | null
  saved: boolean
}

export interface PriceMapsActions {
  draftChanged: (draft: PriceMapDraft) => void
  add: () => void
  addSuggestion: (index: number) => void
  addAllSuggestions: () => void
  remove: (index: number) => void
  save: () => void
  reload: () => void
}

const mapKey = (map: PriceTypeMap): string => `${map.firstResourceId}:${map.firstPriceTypeKey}:${map.secondResourceId}:${map.secondPriceTypeKey}`

/** Выбор вида цены источника: только виды из его ленты цен; лента ещё не прочитана — выбирать не из чего, и окно так и говорит. */
function TypeField({ vm, label, resourceId, value, onChange }: { vm: PriceMapsVm; label: string; resourceId: string; value: string; onChange: (key: string) => void }) {
  const types = resourceId === '' ? [] : vm.typesOf(resourceId)
  const options = [{ value: '', label: 'Не выбран' }, ...types.map((type) => ({ value: type.key, label: type.name ?? type.key }))]
  const reason = resourceId === '' ? 'Сначала выберите источник.' : types.length === 0 ? 'Цены этого источника ещё не прочитаны: видов цен пока нет.' : null
  return <SelectField label={label} value={value} options={options} onChange={onChange} disabled={reason !== null} disabledReason={reason} actionId="sync.price-maps.key" />
}

/**
 * Сопоставление видов цен (требования 15): один раз на пару источников, а не на товар. Сравниваются и показываются расхождениями
 * только сопоставленные виды; один вид цены источника — не больше чем в одной паре с данным источником. Виды цен — из лент цен источников;
 * виды с одинаковым именем у двух источников окно предлагает, человек подтверждает. Сохраняется всё сопоставление целиком — как его видно в окне.
 */
export function PriceMapsScreen({ vm, actions }: { vm: PriceMapsVm; actions: PriceMapsActions }) {
  const sourceOptions = [{ value: '', label: 'Не выбран' }, ...vm.sources.map((source) => ({ value: source.resourceId, label: source.name }))]
  const pair = (map: PriceTypeMap): string =>
    `${vm.nameOf(map.firstResourceId)}: ${vm.typeName(map.firstResourceId, map.firstPriceTypeKey)} ↔ ${vm.nameOf(map.secondResourceId)}: ${vm.typeName(map.secondResourceId, map.secondPriceTypeKey)}`
  return (
    <section aria-labelledby="sync-price-maps-title" className="sync">
      <header>
        <h2 id="sync-price-maps-title">Сопоставление видов цен</h2>
        <p className="sync__lead">
          Укажите, какой вид цены одного источника соответствует какому виду другого: например, «Розничная» в 1С — цена AQSI.
          Несопоставленные виды цен не сравниваются.
        </p>
      </header>

      {vm.status === 'loading' && <StatusMessage kind="loading" title="Загружаем сопоставление…" />}
      {vm.status === 'failed' && vm.loadError !== null && (
        <ErrorBlock
          error={vm.loadError}
          action={
            <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="sync.price-maps.reload">
              Повторить
            </Button>
          }
        />
      )}
      {vm.status === 'ready' && (
        <>
          {vm.maps.length === 0 ? (
            <StatusMessage kind="empty" title="Виды цен ещё не сопоставлены: цены не сравниваются." />
          ) : (
            <div className="sync-table__scroll">
              <table className="sync-table">
                <thead>
                  <tr>
                    <th scope="col">Источник</th>
                    <th scope="col">Вид цены</th>
                    <th scope="col">Источник</th>
                    <th scope="col">Вид цены</th>
                    <th scope="col">
                      <span className="sync__sr">Действие</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vm.maps.map((map, index) => (
                    <tr key={mapKey(map)}>
                      <td>{vm.nameOf(map.firstResourceId)}</td>
                      <td>{vm.typeName(map.firstResourceId, map.firstPriceTypeKey)}</td>
                      <td>{vm.nameOf(map.secondResourceId)}</td>
                      <td>{vm.typeName(map.secondResourceId, map.secondPriceTypeKey)}</td>
                      <td>
                        <Button variant="ghost" icon={<Trash2 size={16} aria-hidden="true" />} onClick={() => actions.remove(index)} actionId="sync.price-maps.remove">
                          Убрать
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {vm.suggestions.length > 0 && (
            <section className="sync__panel" aria-label="Предложения по одинаковому имени">
              <h3>Предложения: одинаковое имя вида цены</h3>
              <p className="sync__note">Система не сопоставляет сама: проверьте пару, добавьте её и сохраните.</p>
              <ul className="sync-list">
                {vm.suggestions.map((suggestion, index) => (
                  <li key={mapKey(suggestion)} className="sync__row">
                    <span>{pair(suggestion)}</span>
                    <Button variant="ghost" icon={<Plus size={16} aria-hidden="true" />} onClick={() => actions.addSuggestion(index)} actionId="sync.price-maps.suggestion-add">
                      Добавить
                    </Button>
                  </li>
                ))}
              </ul>
              <div className="sync__row">
                <Button variant="secondary" icon={<Plus size={16} aria-hidden="true" />} onClick={actions.addAllSuggestions} actionId="sync.price-maps.suggestions-add-all">
                  Добавить все предложения
                </Button>
              </div>
            </section>
          )}

          <section className="sync__panel" aria-label="Новое сопоставление">
            <h3>Добавить пару</h3>
            <div className="sync-apply__fields">
              <SelectField
                label="Первый источник"
                value={vm.draft.firstResourceId}
                options={sourceOptions}
                onChange={(value) => actions.draftChanged({ ...vm.draft, firstResourceId: value, firstPriceTypeKey: '' })}
                actionId="sync.price-maps.source"
              />
              <TypeField
                vm={vm}
                label="Вид цены первого источника"
                resourceId={vm.draft.firstResourceId}
                value={vm.draft.firstPriceTypeKey}
                onChange={(value) => actions.draftChanged({ ...vm.draft, firstPriceTypeKey: value })}
              />
              <SelectField
                label="Второй источник"
                value={vm.draft.secondResourceId}
                options={sourceOptions}
                onChange={(value) => actions.draftChanged({ ...vm.draft, secondResourceId: value, secondPriceTypeKey: '' })}
                actionId="sync.price-maps.source"
              />
              <TypeField
                vm={vm}
                label="Вид цены второго источника"
                resourceId={vm.draft.secondResourceId}
                value={vm.draft.secondPriceTypeKey}
                onChange={(value) => actions.draftChanged({ ...vm.draft, secondPriceTypeKey: value })}
              />
            </div>
            <div className="sync__row">
              <Button variant="secondary" icon={<Plus size={16} aria-hidden="true" />} disabled={vm.draftProblem !== null} disabledReason={vm.draftProblem} onClick={actions.add} actionId="sync.price-maps.add">
                Добавить
              </Button>
            </div>
          </section>

          <div className="sync__row">
            <Button variant="primary" icon={<Save size={16} aria-hidden="true" />} busy={vm.busy} disabled={!vm.dirty} disabledReason={vm.dirty ? null : 'Изменений нет.'} onClick={actions.save} actionId="sync.price-maps.save">
              Сохранить
            </Button>
            {vm.saved && !vm.dirty && (
              <p className="sync__note" role="status">
                Сопоставление сохранено: расхождения цен пересчитаются по нему.
              </p>
            )}
          </div>
          {vm.error !== null && <ErrorBlock error={vm.error} />}
        </>
      )}
    </section>
  )
}
